// Local-first habits hook.
//
// Reads: hydrate from IndexedDB on mount so the UI paints instantly even
// before the network responds, then background-fetch from the API and
// reconcile (replaceAll) the IDB cache + React state.
//
// Writes: every mutation is applied locally first (state + IDB), then
// enqueued to the outbox. The ConnectivityContext owns drain; this hook
// just registers the callbacks needed to (a) swap temp ids for server ids
// when a create resolves and (b) roll back local state if the outbox
// drops a mutation as permanently rejected.

import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { logger } from '../utils/logger';
import { useConnectivity } from '../context/ConnectivityContext';
import { bulkPut, del as idbDel, getAll as idbGetAll, put as idbPut, replaceAll } from '../offline/db';
import { enqueue, newTempHabitId, removeForHabit } from '../offline/outbox';

const log = logger.child('useHabits');

const HABITS_STORE = 'habits';

export const useHabits = (apiUrl, user) => {
  const [habits, setHabits] = useState([]);
  const { notifyEnqueued, registerIdResolver, registerRejectionHandler } = useConnectivity();

  // Hydrate from IDB on mount — synchronous-looking from the UI's POV
  // (single microtask) so the first render shows real data when present.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cached = await idbGetAll(HABITS_STORE);
        if (!cancelled && cached.length > 0) setHabits(cached);
      } catch (e) {
        log.warn('hydrate from idb failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchHabits = useCallback(async () => {
    if (!user?.token) return;
    try {
      const headers = { 'Authorization': `Bearer ${user.token}` };
      const res = await fetchWithRetry(`${apiUrl}/api/habits`, { headers });
      if (!res.ok) throw new Error(`Failed to fetch habits (status ${res.status})`);
      const data = await res.json();
      // Preserve any in-flight temp habits (offline-created, not yet synced) —
      // they'd otherwise vanish on the next refresh while their create is queued.
      const cached = await idbGetAll(HABITS_STORE);
      const tempHabits = cached.filter((h) => typeof h.id === 'string' && h.id.startsWith('tmp_'));
      setHabits([...data, ...tempHabits]);
      await replaceAll(HABITS_STORE, data);
      if (tempHabits.length > 0) await bulkPut(HABITS_STORE, tempHabits);
    } catch (error) {
      log.error('fetchHabits failed', error, { method: 'GET', url: `${apiUrl}/api/habits` });
    }
  }, [apiUrl, user?.token]);

  const addHabit = useCallback(async (name, color, weeklyGoal) => {
    const tempId = newTempHabitId();
    const optimistic = { id: tempId, name, color, weekly_goal: weeklyGoal };
    setHabits((prev) => [...prev, optimistic]);
    try {
      await idbPut(HABITS_STORE, optimistic);
      await enqueue({
        type: 'habit.create',
        payload: { name, color, weekly_goal: weeklyGoal },
        tempId,
      });
      notifyEnqueued();
    } catch (e) {
      log.error('addHabit enqueue failed', e, { name });
      setHabits((prev) => prev.filter((h) => h.id !== tempId));
    }
  }, [notifyEnqueued]);

  const updateHabit = useCallback(async (id, name, color, weeklyGoal) => {
    const next = { id, name, color, weekly_goal: weeklyGoal };
    setHabits((prev) => prev.map((h) => (h.id === id ? { ...h, ...next } : h)));
    try {
      await idbPut(HABITS_STORE, next);
      await enqueue({
        type: 'habit.update',
        payload: next,
        habitRef: typeof id === 'string' && id.startsWith('tmp_') ? id : null,
      });
      notifyEnqueued();
    } catch (e) {
      log.error('updateHabit enqueue failed', e, { id });
    }
  }, [notifyEnqueued]);

  const deleteHabit = useCallback(async (id) => {
    setHabits((prev) => prev.filter((h) => h.id !== id));
    try {
      await idbDel(HABITS_STORE, id);
      // If this was a temp habit, the create is still queued. Drop the create
      // and any dependent toggles instead of round-tripping a delete to the
      // server for a row that never existed there.
      if (typeof id === 'string' && id.startsWith('tmp_')) {
        await removeForHabit(id);
      } else {
        await removeForHabit(id); // also drop any pending toggles for this habit
        await enqueue({
          type: 'habit.delete',
          payload: { id },
        });
      }
      notifyEnqueued();
    } catch (e) {
      log.error('deleteHabit enqueue failed', e, { id });
    }
  }, [notifyEnqueued]);

  // When a temp habit gets a real server id, swap it everywhere this hook
  // owns: React state + IDB row. Cascade rewrites of completion/sickWeek
  // keys (which embed habit_id) are handled by those hooks via their own
  // resolver registrations.
  useEffect(() => {
    return registerIdResolver(async (tempId, realId, serverHabit) => {
      setHabits((prev) => prev.map((h) => (h.id === tempId ? { ...serverHabit } : h)));
      try {
        await idbDel(HABITS_STORE, tempId);
        await idbPut(HABITS_STORE, serverHabit);
      } catch (e) {
        log.warn('idResolver idb update failed', e, { tempId, realId });
      }
    });
  }, [registerIdResolver]);

  // If the outbox permanently rejects a habit mutation (hard 4xx), roll the
  // local state back. For creates: drop the temp habit. For updates/deletes:
  // log; the next background fetch will bring server state back in.
  useEffect(() => {
    return registerRejectionHandler(async (row, status) => {
      if (row.type === 'habit.create' && row.tempId) {
        setHabits((prev) => prev.filter((h) => h.id !== row.tempId));
        try { await idbDel(HABITS_STORE, row.tempId); } catch (_) { /* ignore */ }
        log.warn('habit.create rejected', { tempId: row.tempId, status });
      } else if (row.type === 'habit.update' || row.type === 'habit.delete') {
        log.warn(`${row.type} rejected; next fetch will reconcile`, { status, id: row.payload?.id });
        // Defer to next fetchHabits to bring authoritative state back.
      }
    });
  }, [registerRejectionHandler]);

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  return {
    habits,
    setHabits,
    fetchHabits,
    addHabit,
    updateHabit,
    deleteHabit,
  };
};
